import {
  Button,
  CreateButton,
  DeleteButton,
  EditButton,
  ExportButton,
  Icons,
  Image,
  List,
  notification,
  ShowButton,
  Space,
  Table,
  Tag,
  Typography,
} from '@pankod/refine-antd';
import { IResourceComponentsProps, useNavigation } from '@pankod/refine-core';
import defaultAvatar from 'assets/images/default-avatar.jpg';
import { Search } from 'components';
import { IUser } from 'interfaces';
import { useEffect, useState } from 'react';
import { getUsersWithRole } from 'services/user';
import { getActivityHistoryUrl } from 'utilities/activity-log';
import { downloadCSVFile } from 'utilities/file';
import MetaData from 'utilities/metadata';

const { Text } = Typography;

interface IOptionGroup {
  value: string;
  label: string | React.ReactNode;
}

type SearchKeywords = {
  userCode: string[];
  name: string[];
  email: string[];
  mobileNumber: string[];
};

export const UserList: React.FC<IResourceComponentsProps> = () => {
  const [users, setUsers] = useState<IUser[]>();
  const [keywords, setKeywords] = useState<string>('{}');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExportButtonLoading, setIsExportButtonLoading] = useState<boolean>(false);
  const { push } = useNavigation();

  const fetchDataOnMount = async () => {
    setIsLoading(true);
    try {
      const data = await getUsersWithRole();
      const usersData = data.data;
      const keywordsData = data.keywords;
      setUsers(usersData);
      setKeywords(keywordsData);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchDataOnMount();
  }, []);

  const renderTitle = (title: string) => (
    <div style={{ fontWeight: 'bold' }}>
      <Text style={{ fontSize: '1rem' }}>{title}</Text>
    </div>
  );

  const renderItem = (id: string, title: string) => ({
    value: title,
    label: (
      <div key={id}>
        <Text style={{ marginLeft: '1rem' }}>{title}</Text>
      </div>
    ),
  });

  const getOptionGroup = (options: string[]) => {
    const optionGroup: IOptionGroup[] = [];

    for (let [idx, value] of options.entries()) {
      if (!value) {
        continue;
      }
      optionGroup.push(renderItem(`${idx}`, value));
    }

    return optionGroup;
  };

  const getOptions = () => {
    const parsedKeywords: SearchKeywords = JSON.parse(keywords);
    let userCodeOptionGroup: IOptionGroup[] = [];
    let nameOptionGroup: IOptionGroup[] = [];
    let emailOptionGroup: IOptionGroup[] = [];
    let mobileNumberOptionGroup: IOptionGroup[] = [];

    if (Object.keys(parsedKeywords).length) {
      userCodeOptionGroup = getOptionGroup(parsedKeywords.userCode);
      nameOptionGroup = getOptionGroup(parsedKeywords.name);
      emailOptionGroup = getOptionGroup(parsedKeywords.email);
      mobileNumberOptionGroup = getOptionGroup(parsedKeywords.mobileNumber);
    }

    return [
      {
        label: renderTitle('User Code'),
        options: userCodeOptionGroup,
      },
      {
        label: renderTitle('Name'),
        options: nameOptionGroup,
      },
      {
        label: renderTitle('Email'),
        options: emailOptionGroup,
      },
      {
        label: renderTitle('Mobile Number'),
        options: mobileNumberOptionGroup,
      },
    ];
  };

  const onSelectOption = async (searchText: string) => {
    setIsLoading(true);
    try {
      const data = await getUsersWithRole(searchText);
      const usersData = data.data;
      const keywordsData = data.keywords;
      setUsers(usersData);
      setKeywords(keywordsData);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  };

  const onDeleteSuccessHandler = async () => {
    try {
      const data = await getUsersWithRole();
      const usersData = data.data;
      const keywordsData = data.keywords;
      setUsers(usersData);
      setKeywords(keywordsData);
    } catch (err) {
      return notification.error({
        message: 'Failed',
        description: 'Failed to delete.',
      });
    }
  };

  const onExportButtonClick = async () => {
    setIsExportButtonLoading(true);
    try {
      const data = await getUsersWithRole();
      const productData = data.data;
      const rows = [['Unique ID', 'Name', 'Email', 'Role']];
      productData.forEach((element) => {
        rows.push([element.userCode, element.name, element.email, element.role]);
      });
      downloadCSVFile(rows);
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to Export CSV File.',
      });
    }
    setIsExportButtonLoading(false);
  };

  const pageHeaders = (
    <Space>
      <CreateButton type="primary" />
      <ExportButton loading={isExportButtonLoading} onClick={onExportButtonClick} />
      <Button icon={<Icons.HistoryOutlined />} onClick={() => push(getActivityHistoryUrl('user'))}>
        Activity
      </Button>
    </Space>
  );

  return (
    <List headerProps={{ extra: pageHeaders }}>
      <MetaData />
      <Search
        placeholder="Search by User Code, Name, Email, Mobile Number"
        size="large"
        className="search"
        options={getOptions()}
        onSelectOption={onSelectOption}
        onReset={fetchDataOnMount}
      />
      <div style={{ border: '1px solid #DEEEF8', borderRadius: '4px', overflow: 'hidden' }}>
        <Table dataSource={users} loading={isLoading} rowKey="id" scroll={{ x: true }} rowClassName={(record) => (record.platformMemberId === null ? 'disabled-row' : '')}>
          <Table.Column dataIndex="userCode" title="Unique ID" />
          <Table.Column<IUser>
            dataIndex="avatar"
            title="Logo"
            width="6%"
            render={(_text, record): React.ReactNode => {
              const userPhoto = record?.userPhoto;
              const avatarURL = userPhoto[userPhoto?.length - 1]?.avatar?.thumbUrl;

              return (
                <Image
                  src={avatarURL || defaultAvatar}
                  style={{ objectFit: 'cover' }}
                  height={45}
                  width={45}
                  alt={record.name}
                />
              );
            }}
          />
          <Table.Column dataIndex="name" title="Name" />
          <Table.Column dataIndex="email" title="Email" />
          <Table.Column dataIndex={['role', 'name']} title="Role" />
          <Table.Column<IUser>
            title="Status"
            dataIndex="status"
            render={(_text, record): React.ReactNode => {
              if (record.platformMemberId === null) {
                return <Tag color="blue">Invitation Sent</Tag>;
              }
              return null;
            }}
          />
          <Table.Column<IUser>
            title="Actions"
            dataIndex="actions"
            render={(_text, record): React.ReactNode => {
              const isNonEditableOrDeletable = record.platformMemberId === null;
              return (
                <Space>
                  <ShowButton size="small" recordItemId={record.id} hideText />
                  <EditButton size="small" recordItemId={record.id} hideText disabled={isNonEditableOrDeletable} />
                  <DeleteButton
                    size="small"
                    hideText
                    disabled={isNonEditableOrDeletable}
                    recordItemId={record.id}
                    onSuccess={onDeleteSuccessHandler}
                  />
                </Space>
              );
            }}
          />
        </Table>
      </div>
    </List>
  );
};
